// Admin configuration: the editable label catalog plus a read-only view of the
// configured brands and lifecycle statuses. (SLA targets live in their own
// "SLAs" section; statuses/brands/categories are editable via the backend.)
import { useEffect, useState } from "react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { brand as themeBrand } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import {
  getSettings, updateSettings,
  type ServiceDeskSettings, type LabelOption, type Appearance,
  type AiAgentSettings, type TicketPriority,
} from "../services/serviceDeskService";
import { AiFirstReplySettingsCard } from "../components/AiFirstReplySettingsCard";
import { GoogleProofCard } from "../components/GoogleProofCard";

const DEFAULT_APPEARANCE: Appearance = {
  primaryColor: "",
  headerTitle: "Mesa de Servicio",
  headerSubtitle: "Service Desk",
  logoUrl: "",
  portalIntro: "",
};

// Debe coincidir con DEFAULT_AI_AGENT del backend (serviceDeskConstants.js).
const DEFAULT_AI_AGENT: AiAgentSettings = {
  enabled: false,
  maxAttempts: 2,
  leaseMinutes: 120,
  eligibleLabels: ["bug", "feature_request", "incident"],
  eligibleCategories: ["technical", "feature"],
  minPriority: null,
  requireApproval: true,
};

export default function SettingsSection() {
  const { t } = useTranslation("serviceDesk");
  const { isDarkMode } = useTheme();
  const [settings, setSettings] = useState<ServiceDeskSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getSettings().then(setSettings).catch(() => {}); }, []);

  const save = async (patch: Partial<ServiceDeskSettings>) => {
    setSaving(true);
    try {
      const updated = await updateSettings(patch);
      setSettings(updated);
      Swal.fire({ icon: "success", title: t("settings.saved"), timer: 1200, showConfirmButton: false, background: isDarkMode ? "#1f2937" : "#fff", color: isDarkMode ? "#fff" : "#1f2937" });
    } finally {
      setSaving(false);
    }
  };

  const card = isDarkMode ? "bg-dark-surface border-dark-border" : "bg-white border-gray-200";
  const text = isDarkMode ? "text-white" : "text-gray-900";
  const muted = isDarkMode ? "text-gray-400" : "text-gray-500";
  const txtCls = `flex-1 rounded-lg border px-2 py-1.5 text-sm ${isDarkMode ? "!bg-gray-800 !border-gray-600 !text-white placeholder:!text-gray-400" : "!bg-white !border-gray-300 placeholder:!text-gray-500"}`;

  if (!settings) return <div className="p-8 text-center text-gray-400">Cargando…</div>;

  const appearance: Appearance = { ...DEFAULT_APPEARANCE, ...(settings.appearance || {}) };
  const setAppearance = (patch: Partial<Appearance>) =>
    setSettings((s) => s && ({ ...s, appearance: { ...DEFAULT_APPEARANCE, ...(s.appearance || {}), ...patch } }));

  const slaBreachEmails = (settings.slaBreachNotifyEmails || []).join("\n");
  const setSlaBreachEmails = (text: string) => {
    const emails = text.split("\n").map((e) => e.trim()).filter(Boolean);
    setSettings((s) => s && { ...s, slaBreachNotifyEmails: emails });
  };

  const aiAgent: AiAgentSettings = { ...DEFAULT_AI_AGENT, ...(settings.aiAgent || {}) };
  const setAiAgent = (patch: Partial<AiAgentSettings>) =>
    setSettings((s) => s && ({ ...s, aiAgent: { ...DEFAULT_AI_AGENT, ...(s.aiAgent || {}), ...patch } }));
  const toggleIn = (list: string[], id: string) =>
    list.includes(id) ? list.filter((x) => x !== id) : [...list, id];

  const addLabel = async () => {
    const { value } = await Swal.fire({
      title: t("settings.labels.modalTitle"),
      html: '<input id="l-id" class="swal2-input" placeholder="id (ej. urgente)"><input id="l-label" class="swal2-input" placeholder="Etiqueta visible"><input id="l-color" class="swal2-input" placeholder="#3B82F6" value="#3B82F6">',
      preConfirm: () => ({
        id: (document.getElementById("l-id") as HTMLInputElement)?.value,
        label: (document.getElementById("l-label") as HTMLInputElement)?.value,
        color: (document.getElementById("l-color") as HTMLInputElement)?.value,
      }),
      showCancelButton: true,
      confirmButtonColor: themeBrand.primary,
      background: isDarkMode ? "#1f2937" : "#fff",
      color: isDarkMode ? "#fff" : "#1f2937",
    });
    if (value?.id && value?.label) {
      save({ labels: [...(settings.labels || []), value as LabelOption] });
    }
  };

  const removeLabel = (id: string) => save({ labels: (settings.labels || []).filter((l) => l.id !== id) });

  return (
    <div className="p-6">
      <h1 className={`mb-5 text-xl font-bold ${text}`}>{t("settings.title")}</h1>

      <div className="grid gap-5 lg:grid-cols-2">
        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>{t("settings.appearance.title")}</h2>
          <p className={`mb-4 text-xs ${muted}`}>{t("settings.appearance.description")}</p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <label className={`w-32 text-xs ${muted}`}>{t("settings.appearance.primaryColor")}</label>
              <input
                type="color"
                className="h-8 w-12 cursor-pointer rounded border border-gray-300 bg-transparent"
                value={appearance.primaryColor || "#4F46E5"}
                onChange={(e) => setAppearance({ primaryColor: e.target.value })}
              />
              <input
                className={txtCls}
                placeholder={t("settings.appearance.colorPlaceholder")}
                value={appearance.primaryColor || ""}
                onChange={(e) => setAppearance({ primaryColor: e.target.value })}
              />
            </div>
            <Field label={t("settings.appearance.headerTitle")} value={appearance.headerTitle || ""} onChange={(v) => setAppearance({ headerTitle: v })} placeholder={t("settings.appearance.headerTitlePlaceholder")} muted={muted} txtCls={txtCls} />
            <Field label={t("settings.appearance.headerSubtitle")} value={appearance.headerSubtitle || ""} onChange={(v) => setAppearance({ headerSubtitle: v })} placeholder={t("settings.appearance.headerSubtitlePlaceholder")} muted={muted} txtCls={txtCls} />
            <Field label={t("settings.appearance.logoUrl")} value={appearance.logoUrl || ""} onChange={(v) => setAppearance({ logoUrl: v })} placeholder={t("settings.appearance.logoUrlPlaceholder")} muted={muted} txtCls={txtCls} />
            <div className="flex items-start gap-3">
              <label className={`w-32 pt-2 text-xs ${muted}`}>{t("settings.appearance.portalIntro")}</label>
              <textarea
                className={`${txtCls} h-16 resize-none`}
                placeholder={t("settings.appearance.portalIntroPlaceholder")}
                value={appearance.portalIntro || ""}
                onChange={(e) => setAppearance({ portalIntro: e.target.value })}
              />
            </div>
          </div>
          <button
            onClick={() => save({ appearance })}
            disabled={saving}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 hover:opacity-90"
            style={{ backgroundColor: appearance.primaryColor || themeBrand.primary }}
          >
            {t("settings.appearance.save")}
          </button>
        </div>

        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>{t("settings.publicSite.title")}</h2>
          <p className={`mb-4 text-xs ${muted}`}>{t("settings.publicSite.description")}</p>
          <div className={`flex items-center justify-between rounded-lg border px-3 py-2 ${isDarkMode ? "border-dark-border" : "border-gray-200"}`}>
            <code className={`text-xs ${muted}`}>/soporte</code>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigator.clipboard?.writeText(`${window.location.origin}/soporte`)}
                className="text-xs text-gray-400 hover:underline"
              >
                {t("settings.publicSite.copyLink")}
              </button>
              <a
                href="/soporte"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90"
                style={{ backgroundColor: appearance.primaryColor || themeBrand.primary }}
              >
                <i className="ri-external-link-line" /> {t("settings.publicSite.openSite")}
              </a>
            </div>
          </div>
        </div>

        {/* Agente IA — el runner local (sd-agent.mjs) lee esta configuración
            desde /serviceDesk/ai/queue en cada corrida. */}
        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>{t("settings.aiAgent.title")}</h2>
          <p className={`mb-4 text-xs ${muted}`}>{t("settings.aiAgent.description")}</p>

          <label className={`mb-4 flex items-center gap-2 text-sm ${text}`}>
            <input
              type="checkbox"
              checked={aiAgent.enabled}
              onChange={(e) => setAiAgent({ enabled: e.target.checked })}
            />
            {t("settings.aiAgent.enable")}
          </label>

          <label className={`mb-1 flex items-center gap-2 text-sm ${text}`}>
            <input
              type="checkbox"
              checked={aiAgent.requireApproval !== false}
              onChange={(e) => setAiAgent({ requireApproval: e.target.checked })}
            />
            {t("settings.aiAgent.requireApproval")}
          </label>
          <p className={`mb-4 text-xs ${muted}`}>{t("settings.aiAgent.requireApprovalHint")}</p>

          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <label className={`w-32 text-xs ${muted}`}>{t("settings.aiAgent.maxAttempts")}</label>
              <input
                type="number" min={1} max={5} className={txtCls}
                value={aiAgent.maxAttempts}
                onChange={(e) => setAiAgent({ maxAttempts: Number(e.target.value) || 1 })}
              />
            </div>
            <div className="flex items-center gap-3">
              <label className={`w-32 text-xs ${muted}`}>{t("settings.aiAgent.leaseMinutes")}</label>
              <input
                type="number" min={15} max={480} className={txtCls}
                value={aiAgent.leaseMinutes}
                onChange={(e) => setAiAgent({ leaseMinutes: Number(e.target.value) || 15 })}
              />
            </div>
            <div className="flex items-center gap-3">
              <label className={`w-32 text-xs ${muted}`}>{t("settings.aiAgent.minPriority")}</label>
              <select
                className={txtCls}
                value={aiAgent.minPriority || ""}
                onChange={(e) => setAiAgent({ minPriority: (e.target.value || null) as TicketPriority | null })}
              >
                <option value="">{t("settings.aiAgent.allPriorities")}</option>
                <option value="low">{t("ticketDetail.management.priorities.low")}</option>
                <option value="medium">{t("ticketDetail.management.priorities.medium")}</option>
                <option value="high">{t("ticketDetail.management.priorities.high")}</option>
                <option value="urgent">{t("ticketDetail.management.priorities.urgent")}</option>
              </select>
            </div>

            <div>
              <span className={`mb-1.5 block text-xs ${muted}`}>{t("settings.aiAgent.eligibleLabels")}</span>
              <div className="flex flex-wrap gap-2">
                {(settings.labels || []).map((l) => {
                  const active = aiAgent.eligibleLabels.includes(l.id);
                  return (
                    <button
                      key={l.id}
                      onClick={() => setAiAgent({ eligibleLabels: toggleIn(aiAgent.eligibleLabels, l.id) })}
                      className="rounded-full px-2.5 py-1 text-xs font-medium"
                      style={{ color: active ? "#fff" : l.color, backgroundColor: active ? l.color : `${l.color}1a` }}
                    >
                      {l.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <span className={`mb-1.5 block text-xs ${muted}`}>{t("settings.aiAgent.eligibleCategories")}</span>
              <div className="flex flex-wrap gap-2">
                {(settings.categories || []).map((c) => {
                  const active = aiAgent.eligibleCategories.includes(c.id);
                  return (
                    <button
                      key={c.id}
                      onClick={() => setAiAgent({ eligibleCategories: toggleIn(aiAgent.eligibleCategories, c.id) })}
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${active ? "text-white" : "text-gray-500"}`}
                      style={{ backgroundColor: active ? themeBrand.primary : "#9ca3af1a" }}
                    >
                      {c.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <button
            onClick={() => save({ aiAgent })}
            disabled={saving}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            style={{ backgroundColor: themeBrand.primary }}
          >
            {t("settings.aiAgent.save")}
          </button>
        </div>

        {/* Aprobadores de la IA: correo del dominio probado con Google. */}
        <GoogleProofCard isDarkMode={isDarkMode} primaryColor={appearance.primaryColor || themeBrand.primary} />

        {/* Respuesta con IA de cara al cliente (primera respuesta +
            conversación). Sólo un aprobador la puede guardar. */}
        <AiFirstReplySettingsCard
          value={settings.aiFirstReply}
          labels={settings.labels || []}
          categories={settings.categories || []}
          isDarkMode={isDarkMode}
          primaryColor={appearance.primaryColor || themeBrand.primary}
          saving={saving}
          onSave={(aiFirstReply) => save({ aiFirstReply })}
        />

        <div className={`rounded-xl border p-5 ${card}`}>
          <div className="mb-3 flex items-center justify-between">
            <h2 className={`text-sm font-semibold ${text}`}>{t("settings.labels.title")}</h2>
            <button onClick={addLabel} className={`text-sm font-medium ${isDarkMode ? "text-blue-400" : "text-blue-600"}`}>{t("settings.labels.add")}</button>
          </div>
          <div className="space-y-2">
            {(settings.labels || []).map((l) => (
              <div key={l.id} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${isDarkMode ? "border-gray-600" : "border-gray-200"}`}>
                <span className="inline-flex items-center gap-2 text-sm" style={{ color: l.color }}>
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: l.color }} />
                  {l.label} <span className="text-xs text-gray-400">({l.id})</span>
                </span>
                <button onClick={() => removeLabel(l.id)} className={`text-xs ${isDarkMode ? "text-red-400" : "text-red-500"} hover:underline`}>{t("settings.labels.delete")}</button>
              </div>
            ))}
          </div>
        </div>

        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("settings.brands.title")}</h2>
          <div className="flex flex-wrap gap-2">
            {(settings.brands || []).map((b) => (
              <span key={b.id} className={`rounded-full px-3 py-1 text-sm ${isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-700"}`}>{b.label}</span>
            ))}
          </div>
        </div>

        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-3 text-sm font-semibold ${text}`}>{t("settings.statuses.title")}</h2>
          <div className="flex flex-wrap gap-2">
            {(settings.statuses || []).map((s) => (
              <span key={s.id} className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-sm" style={{ color: s.color, backgroundColor: `${s.color}1a` }}>
                <span className="h-2 w-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.label}
              </span>
            ))}
          </div>
        </div>

        <div className={`rounded-xl border p-5 ${card}`}>
          <h2 className={`mb-1 text-sm font-semibold ${text}`}>{t("settings.slaNotify.title")}</h2>
          <p className={`mb-4 text-xs ${muted}`}>{t("settings.slaNotify.description")}</p>
          <div className="space-y-3">
            <textarea
              className={`${txtCls} h-24 resize-none`}
              placeholder={t("settings.slaNotify.placeholder")}
              value={slaBreachEmails}
              onChange={(e) => setSlaBreachEmails(e.target.value)}
            />
          </div>
          <button
            onClick={() => save({ slaBreachNotifyEmails: (settings.slaBreachNotifyEmails || []) })}
            disabled={saving}
            className="mt-4 rounded-lg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60 hover:opacity-90"
            style={{ backgroundColor: appearance.primaryColor || themeBrand.primary }}
          >
            {t("settings.slaNotify.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, muted, txtCls }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; muted: string; txtCls: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <label className={`w-32 text-xs ${muted}`}>{label}</label>
      <input className={txtCls} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}
